import { useEffect, useMemo, useState, type FC } from 'react'

import { config } from '@skytech/config'
import { Button, Checkbox, InfoBox, LoaderCircle, Skeleton } from '@skytech/ui'
import { getPricePerInstallment } from '@skytech/utils'
import { useQueries, useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'
import BackIcon from 'mdi-react/ArrowLeftIcon'
import CartOutlineIcon from 'mdi-react/CartOutlineIcon'
import CheckIcon from 'mdi-react/CheckIcon'
import { useLocation, useNavigate } from 'react-router-dom'

import queries from '@/api/queries'
import { useTotalSummary } from '@/components/ecom-policy/hooks/use-total-summary'
import type { GetMandatoryProductsResponse } from '@/components/ecom-policy/types'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Page from '@/components/page/Page'
import { useCart } from '@/contexts/cart/cart-context'
import { ecomPolicyResources } from '@/resources/ecom-policy'
import type { MasterOrderAssetRequest } from '@/resources/ecom-policy-orders'
import { APP_ROUTES } from '@/util/appRoutes'

import { useCartOrder } from '../ecom-policy/hooks/use-cart-order'

import { AddressBox } from './AddressBox'
import { AgreedAssetsSummary } from './AgreedAssetsSummary'
import { CartPolicyList } from './CartPolicyList'
import { CartTotalSummary } from './CartTotalSummary'
import { EmptyCart } from './EmptyCart'
import { RemoveProductConfirmation } from './RemoveProductConfirmation'

type PendingRemove = {
  policyId: number
  productId: number
  isLastMainProduct: boolean
}

export const Cart: FC = () => {
  const isDevelopmentMode = config.ENVIRONMENT === 'dev'
  const navigate = useNavigate()
  const location = useLocation()
  const translate = useTranslation()
  let { assetsState = [] }: { assetsState: MasterOrderAssetRequest[] } = location.state || {}
  const {
    getItemsGroupedByPolicy,
    getItemsByPolicy,
    removeItem,
    removeGreenpoolItem,
    setIsTermsAccepted,
    isTermsAccepted,
    items,
  } = useCart()
  const {
    returnAssets,
    buyoutAssets,
    isOrderValid,
    isLoadingPolicy,
    isMasterOrderSubmitting,
    isGreenpoolOrderSubmitting,
    invalidProductPrices,
    doPriceValidation,
    onCompleteOrder,
  } = useCartOrder()
  const {
    countAssetsTotal,
    getPayrollDeductionCase,
    calculateTotal,
    handleDeductionByVariedIntervalCase,
  } = useTotalSummary()
  const groupedItems = getItemsGroupedByPolicy()
  const isOnlyGreenpoolProducts = useMemo(
    () => items.every(item => item.isGreenpoolProduct),
    [items]
  )
  const groupedItemsWithoutGreenpool = Object.fromEntries(
    Object.entries(groupedItems).filter(([, items]) =>
      items.every(item => !item.isGreenpoolProduct)
    )
  )

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingRemove, setPendingRemove] = useState<PendingRemove | null>(null)

  const hasPriceChanges = useMemo(
    () => invalidProductPrices && Object.keys(invalidProductPrices).length > 0,
    [invalidProductPrices]
  )

  const { data: policiesData, isLoading: isLoadingPolicies } = useQuery(
    queries.policyList(),
    ecomPolicyResources.getList
  )

  const policiesQueriesWithBuyout = useQueries({
    queries: assetsState.map(({ policyId }) => ({
      queryKey: queries.policy(Number(policyId)),
      queryFn: () => ecomPolicyResources.get(Number(policyId)),
      enabled: assetsState.length > 0,
    })),
  })
  const isPoliciesWithBuyoutLoading = policiesQueriesWithBuyout.some(q => q.isLoading)
  const policiesAssets = policiesQueriesWithBuyout.flatMap(q => q.data?.assets || [])

  const mandatoryProductsQueries = useQueries({
    queries: Object.entries(groupedItemsWithoutGreenpool).map(([policyId, items]) => ({
      queryKey: queries.mandatoryProducts(
        Number(policyId),
        items.map(item => Number(item.productId))
      ),
      queryFn: (): Promise<GetMandatoryProductsResponse> =>
        ecomPolicyResources.getMandatoryProducts(
          Number(policyId),
          items.map(item => Number(item.productId))
        ),
      onError: () => {
        throw new Error(translate('Failed to load mandatory products'))
      },
    })),
  })

  useEffect(() => {
    if (items.length > 0 && isDevelopmentMode) {
      doPriceValidation()
    }
  }, [items, isDevelopmentMode])

  const onRemoveItem = async (policyId: number, productId: number, force = false) => {
    const policyItems = getItemsByPolicy(policyId)
    if (policyItems.length === 0) return

    const itemToRemove = policyItems.find(item => Number(item.productId) === productId)
    if (!itemToRemove) return

    // If we're not forcing (no confirmation yet), calculate and show confirmation modal
    if (!force) {
      if (itemToRemove.isGreenpoolProduct) {
        // Store state for modal
        setPendingRemove({ policyId, productId, isLastMainProduct: false })
        setShowConfirmModal(true)
        return
      }

      const isRemovedItemAccessory = itemToRemove.product?.isAccessory

      const remainingItems = policyItems.filter(item => Number(item.productId) !== productId)

      const remainingNonMandatoryNonAccessoryItems = remainingItems.filter(
        item => !item.product?.isMandatory && !item.product?.isAccessory
      )

      const isLastMainProduct =
        !isRemovedItemAccessory && remainingNonMandatoryNonAccessoryItems.length === 0

      // Store state for modal
      setPendingRemove({ policyId, productId, isLastMainProduct })
      setShowConfirmModal(true)
      return
    }

    // === Actual removal logic (same as before) ===
    if (itemToRemove.isGreenpoolProduct) {
      removeGreenpoolItem(policyId, productId)
    } else {
      removeItem(policyId, productId)
    }

    const remainingItems = policyItems.filter(item => Number(item.productId) !== productId)
    const isRemovedItemAccessory = itemToRemove.product?.isAccessory

    if (!isRemovedItemAccessory) {
      const remainingNonMandatoryNonAccessoryItems = remainingItems.filter(
        item => !item.product?.isMandatory && !item.product?.isAccessory
      )

      // remove all items if only mandatory and accessory items are left
      if (remainingNonMandatoryNonAccessoryItems.length === 0) {
        remainingItems.forEach(item => removeItem(policyId, Number(item.productId)))
        return
      }
    }

    const remainingNonMandatoryIds = remainingItems
      .filter(item => !item.product?.isMandatory)
      .map(item => Number(item.productId))

    if (remainingNonMandatoryIds.length === 0) {
      const mandatoryItems = policyItems.filter(item => item.product?.isMandatory)
      mandatoryItems.forEach(item => removeItem(policyId, Number(item.productId)))
      return
    }
  }

  const handleCancelRemove = () => {
    setShowConfirmModal(false)
    setPendingRemove(null)
  }
  const handleConfirmRemove = () => {
    if (!pendingRemove) return
    onRemoveItem(pendingRemove.policyId, pendingRemove.productId, true)
    setShowConfirmModal(false)
    setPendingRemove(null)
  }

  const onBack = () => {
    assetsState.length > 0
      ? navigate(APP_ROUTES.cart, { state: { oldAssetsState: assetsState } })
      : navigate(APP_ROUTES.ecomPolicyList)
  }

  const assetsTotal = useMemo(
    () => countAssetsTotal(assetsState, policiesAssets),
    [assetsState, countAssetsTotal, policiesAssets]
  )

  // Calculate totals across all policies
  const { totalPayrollDeduction, grossSalary, interval, totalDeductionByInterval } = useMemo(() => {
    let interval: number | string | undefined = getPayrollDeductionCase(
      policiesData?.policyList || [],
      assetsTotal
    )
    let totalDeductionByInterval: { monthlyPrice: number; paymentInterval: number }[] = []

    const { totalDeduction, grossSalary, totalByInterval, newInterval } = calculateTotal({
      interval,
      policyList: policiesData?.policyList || [],
      invalidProductPrices,
    })

    // 'Same interval' case, asset total === 0
    if (typeof newInterval === 'number') {
      totalDeductionByInterval = [
        {
          monthlyPrice: getPricePerInstallment(totalDeduction, newInterval),
          paymentInterval: newInterval,
        },
      ]
    }

    // 'Varied' case
    if (newInterval === 'varied') {
      totalDeductionByInterval = handleDeductionByVariedIntervalCase(totalByInterval, assetsTotal)
    }

    return {
      totalPayrollDeduction: totalDeduction + assetsTotal,
      grossSalary,
      interval: newInterval,
      totalDeductionByInterval,
    }
  }, [
    getPayrollDeductionCase,
    policiesData?.policyList,
    assetsTotal,
    calculateTotal,
    invalidProductPrices,
    handleDeductionByVariedIntervalCase,
  ])

  const getPendingProductName = (pendingRemove: PendingRemove | null) => {
    if (!pendingRemove) return translate('Unknown Product')
    const pendingProduct = getItemsByPolicy(pendingRemove?.policyId || '').find(
      item => Number(item.productId) === pendingRemove?.productId
    )
    if (pendingProduct?.isGreenpoolProduct) {
      return pendingProduct.asset?.assetName || translate('Unknown Product')
    }
    return (
      pendingProduct?.product?.alias ||
      pendingProduct?.product?.name ||
      translate('Unknown Product')
    )
  }

  if (Object.keys(groupedItems).length === 0 || items.length === 0) {
    return <EmptyCart />
  }

  if (isLoadingPolicies || isPoliciesWithBuyoutLoading) {
    return (
      <Page pageWrapperClassName="max-w-5xl" translate="no">
        <h2 className="!m-0 text-center !text-lg sm:!mb-2 sm:!text-2xl">
          <Translation id="Your Order" />
        </h2>

        <LoaderCircle />
      </Page>
    )
  }

  return (
    <Page pageWrapperClassName="max-w-5xl" translate="no">
      <h2 className="!m-0 text-center !text-lg sm:!mb-2 sm:!text-2xl">
        <Translation id="Your Order" />
      </h2>

      {hasPriceChanges && (
        <InfoBox emphasis="warning" className="mb-2 text-base text-xs sm:mb-4 sm:text-sm">
          <AlertIcon />
          <Translation id="Some prices have changed since you made your selection. Please review the updated prices and payroll deductions before placing your order." />
        </InfoBox>
      )}

      {/* Summary Cost  */}
      <div className="mb-2 flex flex-col gap-2 sm:mb-4">
        <CartTotalSummary
          totalPayrollDeduction={totalPayrollDeduction}
          grossSalary={grossSalary}
          interval={interval}
          totalDeductionByInterval={totalDeductionByInterval}
        />
      </div>

      {/* Cart items will be listed here */}
      <CartPolicyList
        mandatoryProductsQueries={mandatoryProductsQueries}
        invalidProductPrices={invalidProductPrices}
        onRemoveItem={onRemoveItem}
      />

      <h2 className="!mx-0 !my-2 !mt-4 !text-left !text-lg sm:!mb-2 sm:!text-xl">
        <Translation id="Complete the order:" />
      </h2>

      {!isOnlyGreenpoolProducts &&
        (isLoadingPolicy ? (
          <Skeleton height={40} className="mobile:w-80 w-full" />
        ) : (
          <AddressBox />
        ))}

      {/* Previously agreed upon assets */}
      {(buyoutAssets.length > 0 || returnAssets.length > 0) && (
        <AgreedAssetsSummary
          buyoutAssets={buyoutAssets}
          returnAssets={returnAssets}
          policiesQueriesWithBuyout={policiesQueriesWithBuyout}
        />
      )}

      <h3 className="!m-0 !my-2 !text-left !text-lg md:!text-xl">
        <Translation id="VILLKÅR FOR BESTILLING" />
      </h3>
      <div>
        <Checkbox
          id="terms-check-container"
          marginTop={0}
          checked={isTermsAccepted}
          onChange={() => setIsTermsAccepted(!isTermsAccepted)}
          label={
            totalPayrollDeduction === 0 && grossSalary.length === 0 ? (
              <Translation id="This order is placed in accordance with the company’s policies. The full cost is covered by the employer, and the equipment remains the property of the company." />
            ) : (
              <Translation id="Arbeidstaker gir ved denne aksept arbeidsgiver fullmakt til å foreta lønntrekk for kostnader tilknyttet denne bestillingen. Jf. Arbeidsmiljøloven § 14-15 annet ledd bokstav c." />
            )
          }
        />
      </div>

      <div className="my-2 flex w-full flex-col justify-center sm:flex-row sm:py-4">
        <Button
          className="max-lg:flex-1"
          variant="secondary"
          onClick={onBack}
          Icon={assetsState.length > 0 ? <BackIcon /> : <CartOutlineIcon />}
        >
          {assetsState.length > 0 ? <Translation id="Go back" /> : <Translation id="Shop more" />}
        </Button>
        <Button
          className="max-lg:flex-1"
          variant="primary"
          disabled={!isOrderValid}
          isLoading={isMasterOrderSubmitting || isGreenpoolOrderSubmitting}
          onClick={() => onCompleteOrder(mandatoryProductsQueries)}
          isIconRight
          Icon={<CheckIcon />}
        >
          <Translation id="Complete order" />
        </Button>
      </div>

      <RemoveProductConfirmation
        isOpen={showConfirmModal}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        policyName={
          policiesData?.policyList.find(p => p.id === pendingRemove?.policyId)?.name ||
          translate('Unknown Policy')
        }
        productName={getPendingProductName(pendingRemove)}
        isLastMainProduct={pendingRemove?.isLastMainProduct || false}
        translate={translate}
      />
    </Page>
  )
}
